import React, { useState, Fragment } from "react";
import { Link } from "react-router";
import { connect } from "react-redux";
import Highlighter from "react-highlight-words";
import { Menu, Transition } from "@headlessui/react";
import {
  FilterIcon,
  TwoWayLinkIcon,
  OneWayLinkIcon,
  CaretUpSolidIcon,
  CaretDownSolidIcon,
  CopyIcon,
  LinkIcon,
} from "../../../assets/icons/index";
import { DotsVerticalIcon } from "@heroicons/react/solid";
import Loader from "../../shared/loader/loader";
import { toast } from "react-toastify";
import constant from "../../../utils/constant";
import Copy from "../../shared/common/copy";
import {
  getStandardMagicMatchList,
  getStandard,
} from "../../../api/standard.service";
import {
  IStandard,
  StandardMagicMatchData,
} from "../../../model/interface/standard";
import { addLinking } from "../../../api/standardLinking.service";

const MagicMatch = (props: any) => {
  const [standardIdText, setStandardIdText] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [sortAscending, setSortAscending] = useState<boolean>(true);
  const [orderByColumn, setOrderByColumn] = useState<string>("");
  const [standards, setStandards] = useState<StandardMagicMatchData[]>([]);
  const [sourceStandardDetails, setSourceStandardDetails] =
    useState<IStandard>();

  function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(" ");
  }

  const filterHandler = async () => {
    setLoading(true);
    try {
      // Start both requests in parallel
      const [standardMagicMatchListResponse, standardResponse] =
        await Promise.all([
          getStandardMagicMatchList(standardIdText),
          getStandard(standardIdText),
        ]);

      // Update state with the results
      setStandards(standardMagicMatchListResponse.data);
      setSourceStandardDetails(standardResponse.data);
    } catch (error) {
      toast.error("Error occurred while fetching data");
      console.error("Error occurred while fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLink = async (
    destinationStandard: number,
    useTwoWayLink = false
  ) => {
    setLoading(true);
    const promises: any = [];
    promises.push(
      addLinking(props.profile?.userId, standardIdText, destinationStandard)
    );

    if (useTwoWayLink) {
      promises.push(
        addLinking(props.profile?.userId, destinationStandard, standardIdText)
      );
    }

    try {
      // Wait for all promises to resolve
      await Promise.all(promises);

      // We mark the standard as linked
      const updatedStandards = standards.map((standard) => {
        if (standard.standardId === destinationStandard) {
          standard.isLinked = true;
        }
        return standard;
      });

      setStandards(updatedStandards);

      toast.success("Selected standard linked successfully");
    } catch (error) {
      toast.error("Error occurred while linking standards");
      console.error("Error occurred while linking standards:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (columnName: string) => {
    setLoading(true);
    setOrderByColumn(columnName);
    setSortAscending(!sortAscending);
    if (!standards.length) return;

    const sortedStandards = [...standards].sort((a, b) => {
      if (a[orderByColumn] < b[orderByColumn]) {
        return sortAscending ? -1 : 1;
      }
      if (a[orderByColumn] > b[orderByColumn]) {
        return sortAscending ? 1 : -1;
      }
      return 0;
    });

    setStandards(sortedStandards);
    setLoading(false);
  };

  return (
    <div>
      {loading && <Loader />}
      <div className="flex-1 overflow-auto">
        <div className=" h-auto min-h-half w-full rounded-bl-3xl bg-site-teal px-8  py-8 pt-0 pb-14 mt-7 ">
          <div className="relative  py-1">
            <div className="overlay-image-main pointer-events-none fixed left-0 top-0 w-screen h-screen z-20"></div>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-light text-gray-900">Magic Match</h3>
            </div>
            <div className="pt-6 border-b pb-6">
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-2">
                  <label className="text-sm text-gray-700">Standard Id</label>
                  <input
                    id="standardId"
                    name="standardId"
                    type="text"
                    autoComplete="off"
                    className="block mt-1 w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                    value={standardIdText || ""}
                    onChange={(e) => setStandardIdText(Number(e.target.value))}
                  />
                </div>
              </div>
              <div className="mt-4">
                <button
                  type="submit"
                  className="inline-flex items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg"
                  onClick={filterHandler}
                >
                  <FilterIcon className="w-3 h-3"></FilterIcon>
                  <span>Filter</span>
                </button>
              </div>
            </div>
            {sourceStandardDetails && (
              <div id="standardContainer" className="mt-4">
                <div className="flex flex-col mt-1">
                  <div className="-my-2 relative  sm:-mx-6 lg:-mx-8">
                    <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                      <div className="shadow border-b border-gray-200 sm:rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-primary-color">
                            <tr>
                              <th
                                scope="col"
                                className="px-1 py-1 font-light whitespace-nowrap text-sm text-center text-white dark:text-white tracking-wider"
                              >
                                <span className="text-center">Id</span>
                              </th>
                              <th
                                scope="col"
                                className="px-1 py-1 font-light whitespace-nowrap text-sm text-center text-white dark:text-white tracking-wider"
                              >
                                <span className="text-center">Code</span>
                              </th>
                              <th
                                scope="col"
                                className="px-3 py-2 font-light whitespace-nowrap text-sm text-center text-white dark:text-white tracking-wider"
                              >
                                <span className="text-center">Description</span>
                              </th>
                              <th
                                scope="col"
                                className="px-2 py-2 font-light whitespace-nowrap text-sm text-center text-white dark:text-white tracking-wider"
                              >
                                <span className="text-center">Domain</span>
                              </th>
                              <th
                                scope="col"
                                className="px-2 py-2 font-light whitespace-nowrap text-sm text-center text-white dark:text-white tracking-wider"
                              >
                                <span className="text-center">Subject</span>
                              </th>
                              <th
                                scope="col"
                                className="px-2 py-2 font-light whitespace-nowrap text-sm text-center text-white dark:text-white tracking-wider"
                              >
                                <span className="text-center">Grade Level</span>
                              </th>
                              <th
                                scope="col"
                                className="px-2 py-2 font-light whitespace-nowrap text-sm text-center text-white dark:text-white tracking-wider"
                              >
                                <span className="text-center">State</span>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              key={sourceStandardDetails.standardId}
                              className="align-middle border-b bg-white"
                            >
                              <td className="px-1 py-2 border-r text-sm text-center font-normal text-gray-500">
                                <Link
                                  to={constant.EDITSTANDARD.replace(
                                    ":id",
                                    sourceStandardDetails.standardId.toString()
                                  )}
                                  className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                                >
                                  {sourceStandardDetails.standardId}
                                </Link>
                              </td>
                              <td className="px-1 py-2 border-r capitalize text-center text-sm font-normal text-gray-500">
                                {sourceStandardDetails.code}
                              </td>
                              <td className="px-5 py-3 border-r flex justify-start items-center text-sm text-left font-normal text-gray-700">
                                {sourceStandardDetails.description}
                              </td>
                              <td className="px-3 py-2 border-r capitalize text-center text-sm font-normal text-gray-500">
                                <Link
                                  to={constant.EDITDOMAIN.replace(
                                    ":id",
                                    sourceStandardDetails.domainId.toString()
                                  )}
                                  className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                                >
                                  {sourceStandardDetails.domainName}
                                </Link>
                              </td>
                              <td className="px-2 py-2 border-r capitalize text-center text-sm font-normal text-gray-500">
                                <Link
                                  to={constant.SUBJECTDETAIL.replace(
                                    ":id",
                                    sourceStandardDetails.subjectId.toString()
                                  )}
                                  className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                                >
                                  {sourceStandardDetails.subjectName}
                                </Link>
                              </td>
                              <td className="px-2 py-2 border-r capitalize text-center text-sm font-normal text-gray-500">
                                {sourceStandardDetails.gradeLevel}
                              </td>
                              <td className="px-1 py-1 border-r capitalize text-center text-sm font-normal text-gray-500">
                                {sourceStandardDetails.stateName}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div id="dataContainer" className="mt-4">
              <div className="flex flex-col mt-1">
                <div className="-my-2 relative  sm:-mx-6 lg:-mx-8">
                  <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div className="shadow border-b border-gray-200 sm:rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-primary-color">
                          <tr>
                            <th
                              scope="col"
                              className="px-1 py-1 font-light whitespace-nowrap text-sm text-center text-white dark:text-white tracking-wider"
                            >
                              <span className="flex gap-2">
                                Id
                                <span className="cursor-pointer">
                                  {!sortAscending &&
                                  orderByColumn === "standardId" ? (
                                    <CaretUpSolidIcon
                                      onClick={() => handleSort("standardId")}
                                      className="w-5 h-5"
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      onClick={() => handleSort("standardId")}
                                      className="w-5 h-5"
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-1 py-1 font-light whitespace-nowrap text-sm text-center text-white dark:text-white tracking-wider"
                            >
                              <span className="flex gap-2">
                                Code
                                <span className="cursor-pointer">
                                  {!sortAscending &&
                                  orderByColumn === "code" ? (
                                    <CaretUpSolidIcon
                                      onClick={() => handleSort("code")}
                                      className="w-5 h-5"
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      onClick={() => handleSort("code")}
                                      className="w-5 h-5"
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 font-light whitespace-nowrap text-sm text-center text-white dark:text-white tracking-wider"
                            >
                              <span className="text-center">Description</span>
                            </th>
                            <th
                              scope="col"
                              className="px-2 py-2 font-light whitespace-nowrap text-sm text-center text-white dark:text-white tracking-wider"
                            >
                              <span className="flex gap-2">
                                Domain
                                <span className="cursor-pointer">
                                  {!sortAscending &&
                                  orderByColumn === "domain" ? (
                                    <CaretUpSolidIcon
                                      onClick={() => handleSort("domain")}
                                      className="w-5 h-5"
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      onClick={() => handleSort("domain")}
                                      className="w-5 h-5"
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-2 py-2 font-light whitespace-nowrap text-sm text-center text-white dark:text-white tracking-wider"
                            >
                              <span className="flex gap-2">
                                Subject
                                <span className="cursor-pointer">
                                  {!sortAscending &&
                                  orderByColumn === "subject" ? (
                                    <CaretUpSolidIcon
                                      onClick={() => handleSort("subject")}
                                      className="w-5 h-5"
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      onClick={() => handleSort("subject")}
                                      className="w-5 h-5"
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-2 py-2 font-light whitespace-nowrap text-sm text-center text-white dark:text-white tracking-wider"
                            >
                              <span className="flex gap-2">
                                Grade Level
                                <span className="cursor-pointer">
                                  {!sortAscending &&
                                  orderByColumn === "grade" ? (
                                    <CaretUpSolidIcon
                                      onClick={() => handleSort("grade")}
                                      className="w-5 h-5"
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      onClick={() => handleSort("grade")}
                                      className="w-5 h-5"
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-2 py-2 font-light whitespace-nowrap text-sm text-center text-white dark:text-white tracking-wider"
                            >
                              <span className="flex gap-2">
                                State
                                <span className="cursor-pointer">
                                  {!sortAscending &&
                                  orderByColumn === "state" ? (
                                    <CaretUpSolidIcon
                                      onClick={() => handleSort("state")}
                                      className="w-5 h-5"
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      onClick={() => handleSort("state")}
                                      className="w-5 h-5"
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-2 py-2 font-light whitespace-nowrap text-sm text-center text-white dark:text-white tracking-wider"
                            >
                              <span className="flex gap-2">
                                Match Count
                                <span className="cursor-pointer">
                                  {!sortAscending &&
                                  orderByColumn === "matchCount" ? (
                                    <CaretUpSolidIcon
                                      onClick={() => handleSort("matchCount")}
                                      className="w-5 h-5"
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      onClick={() => handleSort("matchCount")}
                                      className="w-5 h-5"
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-1 py-1 font-light whitespace-nowrap text-sm text-center text-white dark:text-white tracking-wider"
                            >
                              <span className="flex gap-2">
                                Match percent
                                <span className="cursor-pointer">
                                  {!sortAscending &&
                                  orderByColumn === "matchPercent" ? (
                                    <CaretUpSolidIcon
                                      onClick={() => handleSort("matchPercent")}
                                      className="w-5 h-5"
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      onClick={() => handleSort("matchPercent")}
                                      className="w-5 h-5"
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 font-light whitespace-nowrap text-sm text-center text-white dark:text-white tracking-wider"
                            ></th>
                          </tr>
                        </thead>

                        <tbody>
                          {standards.map((standard, index) => (
                            <tr
                              key={index}
                              className={`align-middle border-b ${
                                index % 2 === 0 ? "bg-white" : "bg-gray-100"
                              }`}
                            >
                              <td className="px-1 py-2 border-r text-sm text-center font-normal text-gray-500">
                                <div className="flex">
                                  <CopyIcon
                                    className="h-5 cursor-pointer"
                                    title="Copy id"
                                    onClick={() => Copy([standard.standardId])}
                                  />
                                  {standard.isLinked && (
                                    <LinkIcon
                                      className={`cursor-not-allowed w-5 ${
                                        standard.isLinked ? "" : "opacity-50"
                                      }`}
                                    />
                                  )}
                                  <Link
                                    to={constant.EDITSTANDARD.replace(
                                      ":id",
                                      standard.standardId.toString()
                                    )}
                                    className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                                  >
                                    {standard.standardId}
                                  </Link>
                                </div>
                              </td>
                              <td className="px-1 py-2 border-r capitalize text-center text-sm font-normal text-gray-500">
                                {standard.code}
                              </td>
                              <td className="px-5 py-3 border-r flex justify-start items-center text-sm text-left font-normal text-gray-700">
                                <Highlighter
                                  highlightClassName="highlight-match"
                                  searchWords={standard.matchingWords.split(
                                    ", "
                                  )}
                                  autoEscape={true}
                                  textToHighlight={standard.description}
                                />
                              </td>
                              <td className="px-3 py-2 border-r capitalize text-center text-sm font-normal text-gray-500">
                                <Link
                                  to={constant.EDITDOMAIN.replace(
                                    ":id",
                                    standard.domainId.toString()
                                  )}
                                  className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                                >
                                  {standard.domain}
                                </Link>
                              </td>
                              <td className="px-2 py-2 border-r capitalize text-center text-sm font-normal text-gray-500">
                                <Link
                                  to={constant.SUBJECTDETAIL.replace(
                                    ":id",
                                    standard.subjectId.toString()
                                  )}
                                  className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                                >
                                  {standard.subject}
                                </Link>
                              </td>
                              <td className="px-2 py-2 border-r capitalize text-center text-sm font-normal text-gray-500">
                                {standard.gradeLevel}
                              </td>
                              <td className="px-1 py-1 border-r capitalize text-center text-sm font-normal text-gray-500">
                                {standard.state}
                              </td>
                              <td className="px-2 py-2 border-r capitalize text-center text-sm font-normal text-gray-500">
                                {standard.matchCount}
                              </td>
                              <td className="px-1 py-1 capitalize text-sm text-center font-normal text-gray-500">
                                {standard.matchPercent}%
                              </td>
                              <td className="flex justify-end items-center px-3 py-2 text-sm text-center font-normal text-gray-700">
                                {!standard.isLinked && (
                                  <div className="flex space-x-2 justify-center items-center">
                                    <Menu
                                      as="div"
                                      className="relative flex justify-end items-center"
                                    >
                                      <Menu.Button className="h-7 bg-white inline-flex items-center justify-center text-gray-400 rounded-full hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bg-primary-color">
                                        <span className="sr-only">
                                          Open options
                                        </span>
                                        <DotsVerticalIcon
                                          className="w-5 h-5"
                                          aria-hidden="true"
                                        />
                                      </Menu.Button>
                                      <Transition
                                        as={Fragment}
                                        enter="transition ease-out duration-100"
                                        enterFrom="transform opacity-0 scale-95"
                                        enterTo="transform opacity-100 scale-100"
                                        leave="transition ease-in duration-75"
                                        leaveFrom="transform opacity-100 scale-100"
                                        leaveTo="transform opacity-0 scale-95"
                                      >
                                        <Menu.Items className="mx-3 origin-top-right absolute right-7 top-0 w-48 mt-1 rounded-md shadow-lg z-10 bg-white ring-1 ring-black ring-opacity-5 divide-y divide-gray-200 focus:outline-none">
                                          {" "}
                                          <div className="py-1">
                                            <Menu.Item>
                                              {({ active }) => (
                                                <a
                                                  onClick={() =>
                                                    handleLink(
                                                      standard.standardId,
                                                      true
                                                    )
                                                  }
                                                  className={classNames(
                                                    active
                                                      ? "bg-gray-100 text-gray-900"
                                                      : "text-gray-700",
                                                    "group flex items-center px-4 py-2 text-sm"
                                                  )}
                                                >
                                                  <TwoWayLinkIcon className="mr-3 h-5 w-5 text-xs font-light cursor-pointer hover:text-secondary-color transaction ease-in-out duration-300" />
                                                  Create 2-way link
                                                </a>
                                              )}
                                            </Menu.Item>
                                          </div>
                                          <div className="py-1">
                                            <Menu.Item>
                                              {({ active }) => (
                                                <a
                                                  onClick={() =>
                                                    handleLink(
                                                      standard.standardId,
                                                      false
                                                    )
                                                  }
                                                  className={classNames(
                                                    active
                                                      ? "bg-gray-100 text-gray-900"
                                                      : "text-gray-700",
                                                    "group flex items-center px-4 py-2 text-sm"
                                                  )}
                                                >
                                                  <OneWayLinkIcon className="mr-3 h-5 w-5 text-xs font-light cursor-pointer hover:text-red-500 transaction ease-in-out duration-300" />
                                                  Create 1-way link
                                                </a>
                                              )}
                                            </Menu.Item>
                                          </div>
                                        </Menu.Items>
                                      </Transition>
                                    </Menu>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return {
    profile: state.profile,
  };
};

export default connect(mapStateToProps, {})(MagicMatch);
